import { useAtom, useAtomValue } from 'jotai';
import callApi from 'lib/callApi';
import { showFailureToast } from 'lib/ToastHelper';
import { useEffect } from 'react';

import {
  bpaScansDataAtom,
  bpaScansDataQueryParamsAtom,
  isBpaQuerystringFiltersInitializedAtom,
} from './state';
import { BPAScanListData } from './types';

/**
 * Fetches BPA scan data whenever query params or initialization state change.
 * Handles loading state, cancellation on unmount, and error toasts.
 */
export const useBPAScanData = (): BPAScanListData => {
  const dataQueryParams = useAtomValue(bpaScansDataQueryParamsAtom);
  const [listingData, setListingData] = useAtom(bpaScansDataAtom);
  const isInitialized = useAtomValue(isBpaQuerystringFiltersInitializedAtom);

  useEffect(() => {
    if (!isInitialized || !dataQueryParams) return;

    let cancelled = false;

    const fetchData = async () => {
      try {
        setListingData((prev) => ({ ...prev, loading: true }));
        const responseData = await callApi(
          '/bpa/scans/',
          'GET',
          dataQueryParams,
        );

        if (!cancelled) {
          setListingData({ ...responseData?.data, loading: false });
        }
      } catch (error) {
        if (!cancelled) {
          const errorData = (error as { data?: unknown })?.data;
          let errorMessage = 'Failed to load BPA scans.';
          if (errorData !== null && typeof errorData === 'object') {
            const firstKey = Object.keys(errorData as object)[0];
            const firstVal = Object.values(errorData as object).flat()[0];
            if (firstKey && firstVal !== undefined) {
              errorMessage = `${firstKey}: ${String(firstVal)}`;
            }
          }
          showFailureToast(errorMessage);
          setListingData({ results: [], count: 0, loading: false });
        }
      }
    };

    void fetchData();

    return () => {
      cancelled = true;
    };
  }, [dataQueryParams, setListingData, isInitialized]);

  return listingData;
};
